import React, {useCallback, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import HubIcon from '@mui/icons-material/Hub';
import {BarElement, CategoryScale, Chart as ChartJS, LinearScale, Title, Tooltip as ChartTooltip} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip);

interface TornadoChartProps {
    cutSets: MinimalCutSet[];
    nodeMap: Map<number, FaultTreeNode>;
    nodeNameMap: Map<number, string>;
    totalProbability: number;
    structuralHubIds?: Set<number>;
    onBarClick?: (baseEventId: number) => void;
    chartRef?: React.RefObject<ChartJS | null>;
}

interface TornadoRow {
    id: number;
    name: string;
    isBarrier: boolean;
    isHub: boolean;
    isSPF: boolean;
    probability: number;
    /** % change when probability is doubled */
    pctDoubled: number;
    /** % change when probability is halved */
    pctHalved: number;
    /** Total swing = |pctDoubled| + |pctHalved| — used for sorting */
    swing: number;
}

function formatPct(val: number): string {
    if (Math.abs(val) < 0.01) return '0%';
    const sign = val > 0 ? '+' : '';
    if (Math.abs(val) >= 100) return `${sign}${val.toFixed(0)}%`;
    if (Math.abs(val) >= 1) return `${sign}${val.toFixed(1)}%`;
    return `${sign}${val.toFixed(2)}%`;
}

const DEFAULT_LIMIT = 20;
const LIMIT_OPTIONS = [10, 20, 50, 100];

const TornadoChart: React.FC<TornadoChartProps> = ({
    cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds, onBarClick, chartRef,
}) => {
    const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
    const rows = useMemo((): TornadoRow[] => {
        if (totalProbability === 0) return [];

        const allIds = new Set<number>();
        cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));

        const result: TornadoRow[] = [];
        for (const id of allIds) {
            const node = nodeMap.get(id);
            const prob = node?.probability ?? 0;
            if (prob <= 0) continue;

            // Compute P(top) with this event doubled
            let totalDoubled = 0;
            let totalHalved = 0;
            for (const cs of cutSets) {
                if (!cs.baseEventIds.has(id)) {
                    totalDoubled += cs.probability;
                    totalHalved += cs.probability;
                } else {
                    // Ratio of new cut set probability to original
                    // Doubling this event multiplies cut set prob by 2, halving by 0.5
                    totalDoubled += cs.probability * 2;
                    totalHalved += cs.probability * 0.5;
                }
            }

            const pctDoubled = ((totalDoubled - totalProbability) / totalProbability) * 100;
            const pctHalved = ((totalHalved - totalProbability) / totalProbability) * 100;

            const containingCS = cutSets.filter((cs) => cs.baseEventIds.has(id));

            result.push({
                id,
                name: nodeNameMap.get(id) || `ID:${id}`,
                isBarrier: node?.nodeClass === 'BARRIER',
                isHub: structuralHubIds?.has(id) ?? false,
                isSPF: containingCS.some((cs) => cs.baseEventIds.size === 1),
                probability: prob,
                pctDoubled,
                pctHalved,
                swing: Math.abs(pctDoubled) + Math.abs(pctHalved),
            });
        }

        return result.sort((a, b) => b.swing - a.swing);
    }, [cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds]);

    const displayedRows = useMemo((): TornadoRow[] => {
        return rows.slice(0, Math.min(rows.length, limit));
    }, [rows, limit]);

    const showSelector = rows.length > DEFAULT_LIMIT;

    const chartData = useMemo(() => ({
        labels: displayedRows.map((r) => r.name),
        datasets: [
            {
                label: 'Doubled (×2)',
                data: displayedRows.map((r) => r.pctDoubled),
                backgroundColor: 'rgba(211, 47, 47, 0.6)',
                borderColor: 'rgba(211, 47, 47, 1)',
                borderWidth: 1,
                barPercentage: 0.4,
                categoryPercentage: 0.8,
            },
            {
                label: 'Halved (×0.5)',
                data: displayedRows.map((r) => r.pctHalved),
                backgroundColor: 'rgba(46, 125, 50, 0.6)',
                borderColor: 'rgba(46, 125, 50, 1)',
                borderWidth: 1,
                barPercentage: 0.4,
                categoryPercentage: 0.8,
            },
        ],
    }), [displayedRows]);

    const maxAbsPct = useMemo((): number => {
        let max = 0;
        for (const r of displayedRows) {
            max = Math.max(max, Math.abs(r.pctDoubled), Math.abs(r.pctHalved));
        }
        return Math.max(max * 1.15, 1);
    }, [displayedRows]);

    const options = useMemo(() => ({
        indexAxis: 'y' as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: { display: false },
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx: { dataset: { label?: string }; raw: unknown }): string => {
                        const val = ctx.raw as number;
                        return `${ctx.dataset.label}: ${formatPct(val)}`;
                    },
                    afterBody: (items: Array<{ dataIndex: number }>): string => {
                        const row = displayedRows[items[0]?.dataIndex];
                        if (!row) return '';
                        return `P = ${row.probability < 0.001 ? row.probability.toExponential(2) : row.probability.toPrecision(3)}`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                position: 'top' as const,
                title: { display: true, text: 'Change in P(top event) (%)' },
                min: -maxAbsPct,
                max: maxAbsPct,
                ticks: {
                    callback: (value: string | number): string => `${Number(value).toFixed(2)}%`,
                    font: { size: 10 },
                },
            },
            y: {
                stacked: true,
                ticks: {
                    font: { size: 11 },
                    autoSkip: false,
                    color: displayedRows.map((r) => r.isHub ? '#ed6c02' : '#666'),
                },
            },
        },
        onClick: (_event: unknown, elements: Array<{ index: number }>): void => {
            if (elements.length > 0 && onBarClick) {
                onBarClick(displayedRows[elements[0].index].id);
            }
        },
    }), [displayedRows, maxAbsPct, onBarClick]);

    const handleExportCSV = useCallback((): void => {
        const header = ['Basic Event', 'Probability', 'Doubled (%)', 'Halved (%)', 'Swing (%)'].join(',');
        const csvRows = rows.map((r) => [
            `"${r.name.replace(/"/g, '""')}"`,
            r.probability.toExponential(2),
            r.pctDoubled.toFixed(2),
            r.pctHalved.toFixed(2),
            r.swing.toFixed(2),
        ].join(','));
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `tornado_chart_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [rows]);

    if (totalProbability === 0) {
        return (
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                    Tornado chart requires non-zero base event probabilities.
                </Typography>
            </Box>
        );
    }

    if (displayedRows.length === 0) return null;

    const chartHeight = Math.max(200, displayedRows.length * 28 + 80);

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1 }}>
                <Typography variant="subtitle2">Tornado Chart</Typography>
                <Tooltip
                    title="Shows how P(top event) changes when each base event's probability is doubled or halved. Events are ranked by total swing — most influential events first."
                    arrow
                >
                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                        (?)
                    </Typography>
                </Tooltip>
                <Tooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            {showSelector && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Show top</Typography>
                    <TextField
                        select
                        size="small"
                        value={limit}
                        onChange={(e): void => setLimit(Number(e.target.value))}
                        sx={{ minWidth: 80, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5 } }}
                    >
                        {LIMIT_OPTIONS.filter((opt) => opt <= rows.length).map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                        <MenuItem value={Infinity}>All ({rows.length})</MenuItem>
                    </TextField>
                    <Typography variant="caption" color="text.secondary">events</Typography>
                </Stack>
            )}
            <Stack direction="row" spacing={2} sx={{ px: 2, pt: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'rgba(211, 47, 47, 0.6)' }} />
                    <Typography variant="caption" color="text.secondary">Probability doubled — risk increase</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'rgba(46, 125, 50, 0.6)' }} />
                    <Typography variant="caption" color="text.secondary">Probability halved — risk reduction</Typography>
                </Stack>
            </Stack>
            {displayedRows.some((r) => r.isSPF || r.isHub) && (
                <Stack direction="row" spacing={1.5} sx={{ px: 2, pt: 0.5 }}>
                    {displayedRows.some((r) => r.isSPF) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <LooksOneIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            <Typography variant="caption" color="text.secondary">SPF</Typography>
                        </Stack>
                    )}
                    {displayedRows.some((r) => r.isHub) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <HubIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                            <Typography variant="caption" color="text.secondary">Structural hub</Typography>
                        </Stack>
                    )}
                </Stack>
            )}
            <Box sx={{ px: 2, py: 1, overflow: 'auto', maxHeight: 500 }}>
                <Box sx={{ height: chartHeight, minHeight: 200 }}>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    <Bar ref={chartRef as any} data={chartData} options={options} />
                </Box>
            </Box>
        </Box>
    );
};

export default TornadoChart;
